/**
 * DigiLockerMockConsent — Simulates the official DigiLocker consent screen.
 * Only used when DIGILOCKER_MODE=mock. In live mode the user is redirected
 * straight to digilocker.gov.in.
 */
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Shield, CheckCircle2, X, Loader2, Lock, FileText, Sparkles } from "lucide-react";

const API = `${window.location.origin}/api/digilocker`;

const SCOPES = [
  { icon: FileText, label: "Aadhaar Card (issuer-signed XML + PDF)" },
  { icon: FileText, label: "PAN Card (issuer-signed PDF)" },
  { icon: FileText, label: "Driving Licence (if available)" },
  { icon: Shield, label: "Basic profile (name, DoB, gender, address)" },
];

export default function DigiLockerMockConsent() {
  const [params] = useSearchParams();
  const state = params.get("state");
  const [busy, setBusy] = useState(false);
  const [aadhaar, setAadhaar] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("login");

  if (!state) {
    return <div style={{ padding: 40, color: "#fff" }}>Invalid session.</div>;
  }

  const sendOtp = () => {
    if (aadhaar.length !== 12) { alert("Aadhaar should be 12 digits"); return; }
    setStep("otp");
  };

  const confirm = (allow) => {
    setBusy(true);
    const url = allow
      ? `${API}/callback?code=mock-${state.slice(0, 8)}&state=${encodeURIComponent(state)}`
      : `${API}/callback?error=access_denied&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  };

  return (
    <div data-testid="digilocker-mock-consent" style={{
      minHeight: "100vh", background: "#0d1117", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 24, fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        maxWidth: 480, width: "100%", background: "#ffffff", borderRadius: 12,
        boxShadow: "0 8px 40px rgba(0,0,0,0.4)", overflow: "hidden",
      }}>
        {/* Header — looks like real DigiLocker */}
        <div style={{ background: "linear-gradient(135deg, #1e40af, #1e3a8a)", padding: "20px 24px", color: "#fff" }}>
          <div className="flex items-center gap-3">
            <div style={{ background: "rgba(255,255,255,0.18)", padding: 8, borderRadius: 8 }}>
              <Shield size={22} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>DigiLocker</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>Government of India · Ministry of Electronics &amp; IT</div>
            </div>
          </div>
        </div>

        {/* Sandbox banner */}
        <div style={{ background: "#fef3c7", borderBottom: "1px solid #fde68a", padding: "10px 16px", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Sparkles size={14} style={{ color: "#92400e", marginTop: 2, flexShrink: 0 }} />
          <div style={{ color: "#92400e", fontSize: 11, lineHeight: 1.5 }}>
            <strong>Sandbox Mode</strong> — yeh ek simulated screen hai. Real production mein aap digilocker.gov.in pe redirect honge. <strong>Demo Aadhaar: 999999999999, OTP: 123456</strong>
          </div>
        </div>

        {step === "login" && (
          <div style={{ padding: 24 }}>
            <h2 style={{ margin: 0, marginBottom: 6, color: "#0f172a", fontSize: 17, fontWeight: 700 }}>Sign in to DigiLocker</h2>
            <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5, margin: 0, marginBottom: 18 }}>
              <strong style={{ color: "#0f172a" }}>Rudrik</strong> is requesting access to your verified documents.
            </p>

            <label style={{ color: "#475569", fontSize: 12, fontWeight: 600, marginBottom: 6, display: "block" }}>Aadhaar Number</label>
            <input
              data-testid="dl-aadhaar-input"
              value={aadhaar}
              onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12))}
              placeholder="12-digit Aadhaar"
              maxLength={12}
              style={{ width: "100%", padding: "11px 14px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, fontFamily: "ui-monospace, monospace", letterSpacing: 2, color: "#0f172a", outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ marginTop: 16 }}>
              <button
                data-testid="dl-send-otp-btn"
                onClick={sendOtp}
                style={{ width: "100%", background: "#1e40af", color: "#fff", border: "none", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              >Send OTP</button>
            </div>
            <div style={{ marginTop: 14, padding: 10, background: "#f1f5f9", borderRadius: 6, fontSize: 11, color: "#475569", display: "flex", gap: 6, alignItems: "flex-start" }}>
              <Lock size={11} style={{ marginTop: 2, color: "#64748b", flexShrink: 0 }} />
              <div>Your Aadhaar is sent over TLS 1.3 and stored only as a one-way hash. Rudrik never sees your full Aadhaar number.</div>
            </div>
          </div>
        )}

        {step === "otp" && (
          <div style={{ padding: 24 }}>
            <h2 style={{ margin: 0, marginBottom: 6, color: "#0f172a", fontSize: 17, fontWeight: 700 }}>Enter OTP</h2>
            <p style={{ color: "#64748b", fontSize: 12, margin: 0, marginBottom: 18 }}>
              We've sent a 6-digit OTP to the mobile linked with Aadhaar XXXX-XXXX-{aadhaar.slice(-4)}.
            </p>
            <input
              data-testid="dl-otp-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit OTP"
              maxLength={6}
              style={{ width: "100%", padding: "11px 14px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 16, fontFamily: "ui-monospace, monospace", letterSpacing: 6, textAlign: "center", color: "#0f172a", outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ marginTop: 16 }}>
              <button
                data-testid="dl-verify-otp-btn"
                onClick={() => { if (otp.length === 6) setStep("consent"); else alert("Enter 6-digit OTP"); }}
                style={{ width: "100%", background: "#1e40af", color: "#fff", border: "none", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              >Verify &amp; Continue</button>
            </div>
          </div>
        )}

        {step === "consent" && (
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ background: "#dbeafe", width: 36, height: 36, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Shield size={18} style={{ color: "#1e40af" }} />
              </div>
              <div>
                <div style={{ color: "#0f172a", fontSize: 15, fontWeight: 700 }}>Rudrik wants access to</div>
                <div style={{ color: "#64748b", fontSize: 11 }}>You'll be returned to Rudrik after confirming</div>
              </div>
            </div>

            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 18px", display: "flex", flexDirection: "column", gap: 8 }}>
              {SCOPES.map((s, i) => {
                const I = s.icon;
                return (
                  <li key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", background: "#f8fafc", borderRadius: 6, fontSize: 12, color: "#334155" }}>
                    <I size={14} style={{ color: "#1e40af", flexShrink: 0 }} />
                    {s.label}
                  </li>
                );
              })}
            </ul>

            <div style={{ fontSize: 10, color: "#64748b", lineHeight: 1.5, marginBottom: 18, padding: 10, background: "#f8fafc", borderRadius: 6 }}>
              By clicking Allow, you grant Rudrik time-limited access to the above. You can revoke this at any time from digilocker.gov.in &gt; Linked Apps.
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                data-testid="dl-deny-btn"
                disabled={busy}
                onClick={() => confirm(false)}
                style={{ flex: 1, background: "#ffffff", color: "#1e40af", border: "1.5px solid #1e40af", padding: "11px 0", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              ><X size={14} /> Deny</button>
              <button
                data-testid="dl-allow-btn"
                disabled={busy}
                onClick={() => confirm(true)}
                style={{ flex: 1, background: "#1e40af", color: "#fff", border: "1.5px solid #1e40af", padding: "11px 0", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                {busy ? "Redirecting..." : "Allow"}
              </button>
            </div>
          </div>
        )}

        <div style={{ background: "#f8fafc", padding: "10px 16px", textAlign: "center", fontSize: 9, color: "#94a3b8", letterSpacing: 0.5 }}>
          A Digital India Initiative · digilocker.gov.in
        </div>
      </div>
    </div>
  );
}
